import type {ItemObject, ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {SlideItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {getUserId, isUserDesigner, isUserLoggedIn} from '@Libraries/user.library';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import type React from 'react';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import type {FabricPositionalParams} from '@Utils/fabric.util';
import {renderFabricObjectToDataUrl, renderFabricObjectToDataUrlOnSolidBackground} from '@Utils/fabric.util';
import {copyTextAndImageToClipboard, copyToClipboard, readFromClipboard} from '@Utils/clipboard.util';
import {dataUrlToBlob} from '@Utils/file.util';
import {ElementDataType, isTempImageData} from '@Libraries/add-media-library';
import {isJson} from '@Utils/string.util';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {ItemData} from './add-item.class';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import {Point} from '@postermywall/fabricjs-2';
import {DEFAULT_TEXT_FONT_FAMILY} from '@PosterWhiteboard/classes/text-styles.class';
import {getRecommendedFont, getScriptForText} from '@Libraries/font-library';
import {
  getMediaStorageForbackendData,
  getSingleUploadedMediaItem,
  type UploadFileDataTypes,
  uploadFiles,
  type UserMediaType,
  type UserMediaVOResponse,
  userMediaVOToElementData,
} from '@Libraries/user-media-library';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import {POSTER_VERSION, type ResizeDimensions} from '@PosterWhiteboard/poster/poster.types';
import {makeShortcut} from '@Utils/shortcut.util';
import {isDesktop} from 'react-device-detect';
import {addQrCodeToPage} from '@PosterWhiteboard/items/qr-code-item.class';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {getVisiblePageBounds, type VisiblePageBounds} from '@PosterWhiteboard/page/page-visibility.util';
import type {PageGroupsObject} from '@PosterWhiteboard/page/page-groups.class';

export const PMW_CUSTOM_CLIPBOARD_MIMETYPE = 'web application/x-postermywall';
export const PLAIN_TEXT_CLIPBOARD_MIMETYPE = 'text/plain';

interface ClipboardPasteInfo {
  isPasteFromRightClick: boolean;
  eventCanvasOffset?: FabricPositionalParams;
}

/**
 * The absolute page point a right-click/long-press paste should land on, plus the copied
 * selection's anchor used to preserve the relative layout of multi-item pastes.
 */
interface RightClickPastePoint {
  x: number;
  y: number;
  selectionLeft: number;
  selectionTop: number;
}

interface PosterClipboardData {
  key: string;
  version: string;
  graphicItemObjects: ItemObject[];
  audioItemObject?: AudioItemObject;
  backgroundItemObject?: ItemObject;
  posterInfo: ClipboardPosterInfo;
  selection: Record<string, number | null>;
  groups?: PageGroupsObject;
}

interface ClipboardPosterInfo {
  width: number;
  height: number;
  idGalleryTemplate?: string;
  idGalleryTemplateCreator: number;
}

/**
 * Makes sure user isn't able to copy-paste items from an older version poster editor where item structure was different
 */
const PASTE_ITEMS_VERSION = `${POSTER_VERSION.CURRENT}_1`;

/**
 * Offset applied per step when the paste target position is already occupied by the copied
 * item (or an earlier paste of it), so the new copy doesn't sit exactly on top of it.
 */
const PASTE_DISPLACEMENT_STEP = 15;

/**
 * Bounds how far a paste cascades away from occupied positions on repeated pastes.
 */
const MAX_PASTE_DISPLACEMENT_STEPS = 20;

/**
 * Tolerance (in page pixels) within which two item positions/sizes count as identical. Item
 * re-initialization can shift a pasted item by a pixel, so this must comfortably exceed 1px
 * while staying well below PASTE_DISPLACEMENT_STEP.
 */
const PASTE_POSITION_MATCH_TOLERANCE = 3;

/**
 * Identity resize adjustment used when source and target poster dimensions match.
 */
const NO_RESIZE_DIMENSIONS: ResizeDimensions = {left: 0, top: 0, scaleFactor: 1};

/**
 * Relative rendered-size difference above which a same-text text item counts as a different
 * design element (noticeably rescaled), letting the paste land at the exact coordinates.
 */
const COPY_MAX_RELATIVE_SIZE_DIFFERENCE = 0.2;

/**
 * When the paste target area is outside the visible canvas, the paste is anchored this far
 * into the visible area (fractions of the visible width/height).
 */
const VISIBLE_PASTE_ANCHOR_X_RATIO = 0.1;
const VISIBLE_PASTE_ANCHOR_Y_RATIO = 0.125;

const COPIED_ITEM_BLOB_MAX_DIMENSION = 1024;
const COPIED_ITEMS_BLOB_BACKGROUND_COLOR = '#ffffff';

export class PageClipboard extends CommonMethods {
  public page: Page;

  public constructor(page: Page) {
    super();
    this.page = page;
  }

  public copySelection(e?: Event | React.MouseEvent<HTMLElement>): void {
    const selection = this.page.getSelectedItems();

    // A background is copied as a background snapshot (so paste restores it as the
    // page background), not as a floating graphic item.
    if (selection.length === 1 && selection[0].isBackground) {
      if (e) {
        e.preventDefault();
      }
      void this.copyBackground(selection[0].uid);
      return;
    }

    if (selection.length) {
      let graphicItems: ItemType[] = [];

      for (let i = 0; i < selection.length; i++) {
        const selectionItem = selection[i];
        graphicItems.push(selectionItem);
      }
      if (graphicItems.length) {
        if (e) {
          e.preventDefault(); // default behaviour is to copy any selected text
        }
        graphicItems = this.page.activeSelection.orderGraphicItems(graphicItems);

        const graphicItemObjects = graphicItems.map((item) => {
          return item.toObject();
        });

        const {poster} = this.page;
        const activeObject = this.page.activeSelection.getActiveObject();
        // activeObject is null when a single item is selected, so fall back to that item's own coordinates
        const copiedGroups: PageGroupsObject['groupsHashMap'] = {};
        for (const item of graphicItems) {
          if (!item.groupId || copiedGroups[item.groupId]) {
            continue;
          }
          const groupRecord = this.page.items.groups.groupsHashMap[item.groupId];
          if (groupRecord) {
            copiedGroups[item.groupId] = groupRecord;
          }
        }

        const copiedItemBlob = this.renderCopiedItemsBlob(graphicItems);

        const graphicItemsAndPoster: PosterClipboardData = {
          key: poster.copyKey,
          graphicItemObjects,
          version: PASTE_ITEMS_VERSION,
          posterInfo: {
            width: poster.width,
            height: poster.height,
            idGalleryTemplate: this.page.galleryTemplateData?.posterId,
            idGalleryTemplateCreator: this.page.galleryTemplateData?.idGalleryTemplateCreator ?? 0,
          },
          selection: {
            left: activeObject ? activeObject.left : graphicItemObjects[0].left,
            top: activeObject ? activeObject.top : graphicItemObjects[0].top,
            width: activeObject ? activeObject.width : null,
            height: activeObject ? activeObject.height : null,
          },
          groups: {groupsHashMap: copiedGroups},
        };
        const jsonString = JSON.stringify(graphicItemsAndPoster);
        void this.writeGraphicItemsToClipboard(jsonString, copiedItemBlob);
      } else {
        getPosterUi().openMessageModal({
          title: window.i18next.t('pmwjs_copying_error_title'),
          text: window.i18next.t('pmwjs_title_copy_disabled'),
        });
      }
    } else {
      void this.checkAndCopyAudioSelection();
    }
  }

  async writeGraphicItemsToClipboard(jsonString: string, copiedItemBlob: Blob | null): Promise<void> {
    if (copiedItemBlob) {
      await copyTextAndImageToClipboard(jsonString, copiedItemBlob);
      return;
    }
    await copyToClipboard(jsonString);
  }

  renderCopiedItemsBlob(graphicItems: ItemType[]): Blob | null {
    for (const item of graphicItems) {
      try {
        const dataUrl = this.renderCopiedItemToDataUrl(item);
        if (dataUrl !== null) {
          return dataUrlToBlob(dataUrl);
        }
      } catch (e) {
        console.error(e);
      }
    }
    return null;
  }

  private renderCopiedItemToDataUrl(item: ItemType): string | null {
    if (this.isTextItem(item) || this.isMotionItem(item) || item.isBackground) {
      return null;
    }
    const imageItem = this.getImageOfCopiedItem(item);
    if (imageItem) {
      return imageItem.hasShapeModifications() ? renderFabricObjectToDataUrl(imageItem.fabricObject, COPIED_ITEM_BLOB_MAX_DIMENSION) : null;
    }
    return renderFabricObjectToDataUrlOnSolidBackground(item.fabricObject, COPIED_ITEMS_BLOB_BACKGROUND_COLOR, COPIED_ITEM_BLOB_MAX_DIMENSION);
  }

  private isMotionItem(item: ItemType): boolean {
    if (item.isVideo() || (item.isSticker() && (item.isAnimatedSticker() || item.isAiAnimatedSticker()))) {
      return true;
    }
    return item.isSlideshow() && (this.getRepresentativeSlide(item)?.isVideoSlide() ?? false);
  }

  private isTextItem(item: ItemType): boolean {
    if (item.isText()) {
      return true;
    }
    return item.isSlideshow() && (this.getRepresentativeSlide(item)?.isTextSlide() ?? false);
  }

  private getImageOfCopiedItem(item: ItemType): ImageItem | null {
    if (item.isImage()) {
      return item;
    }
    if (!item.isSlideshow()) {
      return null;
    }
    const slide = this.getRepresentativeSlide(item);
    return slide?.isImageSlide() ? slide : null;
  }

  private getRepresentativeSlide(slideshow: SlideshowItem): SlideItem | undefined {
    return slideshow.getSelectedSlide() ?? slideshow.slides.slidesHashMap[slideshow.slides.slidesOrder[0]];
  }

  // Serializes a timeline background to the OS clipboard. The bg ItemObject is
  // stashed in `backgroundItemObject`; on paste, `pasteItems` routes to
  // `pasteBackgroundItemIfInClipboard` which slots a clone directly after the
  // background active at the current page time.
  public async copyBackground(uid: string): Promise<void> {
    const bg = this.page.items.itemsHashMap[uid];
    if (!bg?.isBackground) {
      return;
    }
    const {poster} = this.page;
    const jsonString = JSON.stringify({
      key: poster.copyKey,
      version: PASTE_ITEMS_VERSION,
      graphicItemObjects: [],
      backgroundItemObject: bg.toObject(),
      posterInfo: {
        width: poster.width,
        height: poster.height,
        idGalleryTemplate: poster.getGalleryTemplateId(),
        idGalleryTemplateCreator: poster.getGalleryTemplateCreatorId(),
      },
      selection: {},
    } as PosterClipboardData);
    await this.writeGraphicItemsToClipboard(jsonString, this.renderCopiedItemsBlob([bg]));
  }

  public async checkAndCopyAudioSelection(): Promise<void> {
    const audioItem = this.page.audioClips.getSelectedAudioItem();

    if (!audioItem) {
      return;
    }

    const {poster} = this.page;
    const jsonString = JSON.stringify({
      key: poster.copyKey,
      version: PASTE_ITEMS_VERSION,
      graphicItemObjects: [],
      audioItemObject: audioItem.toObject(),
      posterInfo: {
        width: poster.width,
        height: poster.height,
        idGalleryTemplate: poster.getGalleryTemplateId(),
        idGalleryTemplateCreator: poster.getGalleryTemplateCreatorId(),
      },
      selection: {},
    } as PosterClipboardData);
    await copyToClipboard(jsonString);
  }

  public async pasteInternalItemsAndPosterDataIfInClipboard(
    clipboardData: unknown,
    e?: Event | ClipboardEvent | React.MouseEvent<HTMLElement>,
    clipboardPasteInfo: ClipboardPasteInfo = {isPasteFromRightClick: false},
    onSuccess?: () => void
  ): Promise<void> {
    const {poster} = this.page;
    const graphicItemsAndPoster = await getItemsAndPosterDataFromClipboard(clipboardData);
    if (graphicItemsAndPoster && graphicItemsAndPoster.graphicItemObjects.length > 0) {
      if (this.isPasteAllowed(graphicItemsAndPoster)) {
        if (e) {
          e.preventDefault(); // We are already handling the data from the clipboard, we do not want it inserted into the document
        }
        showLoading('pastingGraphicItems');
        const arePosterDimensionsDifferent = poster.width !== graphicItemsAndPoster.posterInfo.width || poster.height !== graphicItemsAndPoster.posterInfo.height;
        const rightClickPastePoint = getRightClickPastePoint(clipboardPasteInfo, graphicItemsAndPoster.selection);
        const graphicItems: ItemType[] = [];

        const graphicItemPromises = [];
        const graphicItemOrderIds = [];

        for (const item of graphicItemsAndPoster.graphicItemObjects) {
          graphicItemOrderIds.push(item.uid);
          graphicItemPromises.push(
            new Promise<void>((resolve, reject) => {
              createItemFromObject(this.page, item)
                .then((graphicItem) => {
                  const templateCreator = graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator;
                  if (!this.isItemPasteAllowed(graphicItemsAndPoster.key, graphicItem.idOriginalOwner, templateCreator)) {
                    hideLoading('pastingGraphicItems');
                    getPosterUi().openErrorModal({
                      title: window.i18next.t('pmwjs_pasting_error_title'),
                      message: window.i18next.t('pmwjs_item_pasting_error'),
                    });
                    reject(new Error('Item not alloed to be pasted'));
                    return;
                  }
                  if (graphicItemsAndPoster.posterInfo.idGalleryTemplate) {
                    graphicItem.idOriginalOwner = templateCreator;
                  }

                  // The resize math floors float artifacts into ±1px offsets, so it only runs
                  // when the source poster's dimensions actually differ.
                  const resizeDimensions = arePosterDimensionsDifferent
                    ? poster.resizePoster.getDimensionsToResizePageItemsToTargetType(graphicItemsAndPoster.posterInfo.width, graphicItemsAndPoster.posterInfo.height, item, {
                        width: poster.width,
                        height: poster.height,
                      })
                    : NO_RESIZE_DIMENSIONS;
                  // Positions derive from the clipboard object's stored coordinates, not the
                  // re-created fabric object's, which item init can shift by a pixel.
                  if (rightClickPastePoint) {
                    graphicItem.fabricObject.setXY(
                      new Point(
                        rightClickPastePoint.x + (item.left - rightClickPastePoint.selectionLeft) * resizeDimensions.scaleFactor,
                        rightClickPastePoint.y + (item.top - rightClickPastePoint.selectionTop) * resizeDimensions.scaleFactor
                      )
                    );
                  } else {
                    // Keyboard paste targets the copied item's exact position.
                    graphicItem.fabricObject.setXY(new Point(item.left + resizeDimensions.left, item.top + resizeDimensions.top));
                  }
                  graphicItem.fabricObject.scaleX *= resizeDimensions.scaleFactor;
                  graphicItem.fabricObject.scaleY *= resizeDimensions.scaleFactor;

                  graphicItems.push(graphicItem);

                  resolve();
                })
                .catch(reject);
            })
          );
        }

        const responses = await Promise.allSettled(graphicItemPromises);

        for (const response of responses) {
          if (response.status === 'rejected') {
            console.error('Item failed to paste details:', response);
          }
        }

        // Rearrange graphicItems to match the order of graphicItemOrderIds
        const itemMap = new Map<string, ItemType>();
        graphicItems.forEach((item) => {
          itemMap.set(item.uid, item);
        });

        const orderedGraphicItems: ItemType[] = [];
        graphicItemOrderIds.forEach((uid) => {
          const item = itemMap.get(uid);
          if (item) {
            orderedGraphicItems.push(item);
          }
        });

        let pasteDisplacement = 0;
        if (!rightClickPastePoint) {
          this.#movePasteIntoVisibleAreaIfHidden(orderedGraphicItems);
          pasteDisplacement = this.#getDisplacementToAvoidPastingOnCopies(orderedGraphicItems);
        }

        this.page.activeSelection.duplicateSelection(orderedGraphicItems, {
          sourceGroupHashMap: graphicItemsAndPoster.groups?.groupsHashMap ?? {},
          x: pasteDisplacement,
          y: pasteDisplacement,
        });
        hideLoading('pastingGraphicItems');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        getPosterUi().openMessageModal({
          title: window.i18next.t('pmwjs_pasting_error_title'),
          text: window.i18next.t('pmwjs_pasting_error'),
        });
      }
    }
  }

  /**
   * When the paste target area is completely outside the visible canvas (zoomed in / panned
   * away), re-anchors the pasted items into the visible area, preserving their relative layout.
   */
  #movePasteIntoVisibleAreaIfHidden(graphicItems: ItemType[]): void {
    if (graphicItems.length === 0) {
      return;
    }
    const visibleBounds = getVisiblePageBounds(this.page);
    if (!visibleBounds) {
      return;
    }
    const pasteBounds = getBoundsOfItems(graphicItems);
    const isPasteAreaVisible =
      pasteBounds.left < visibleBounds.right && pasteBounds.right > visibleBounds.left && pasteBounds.top < visibleBounds.bottom && pasteBounds.bottom > visibleBounds.top;
    if (isPasteAreaVisible) {
      return;
    }
    const anchorX = visibleBounds.left + (visibleBounds.right - visibleBounds.left) * VISIBLE_PASTE_ANCHOR_X_RATIO;
    const anchorY = visibleBounds.top + (visibleBounds.bottom - visibleBounds.top) * VISIBLE_PASTE_ANCHOR_Y_RATIO;
    for (const graphicItem of graphicItems) {
      graphicItem.fabricObject.setXY(
        new Point(anchorX + (graphicItem.fabricObject.getX() - pasteBounds.left), anchorY + (graphicItem.fabricObject.getY() - pasteBounds.top))
      );
    }
  }

  /**
   * Returns the offset to paste at so the new items don't sit exactly on top of the items they
   * were copied from (or earlier pastes of them). The offset cascades on repeated pastes until
   * a free spot is found. A different item occupying the target position doesn't displace the
   * paste — only a copy of the pasted item does.
   */
  #getDisplacementToAvoidPastingOnCopies(graphicItems: ItemType[]): number {
    const existingItems = Object.values(this.page.items.itemsHashMap).filter((item) => !item.isBackground);
    let displacement = 0;
    for (let step = 0; step < MAX_PASTE_DISPLACEMENT_STEPS; step++) {
      const isTargetOccupiedByCopy = graphicItems.some((pastedItem) => {
        return existingItems.some((existingItem) => {
          return isCopyOfItemAtPosition(existingItem, pastedItem, displacement);
        });
      });
      if (!isTargetOccupiedByCopy) {
        break;
      }
      displacement += PASTE_DISPLACEMENT_STEP;
    }
    return displacement;
  }

  public async pasteItems(
    e?: Event | ClipboardEvent | React.MouseEvent<HTMLElement>,
    clipboardPasteInfo: ClipboardPasteInfo = {isPasteFromRightClick: false},
    onSuccess?: () => void
  ): Promise<void> {
    let file: File | undefined;
    if (e instanceof ClipboardEvent && e.clipboardData?.files) {
      const filesArray = Array.from(e.clipboardData.files);
      file = filesArray.find((item: File) => item.type.includes('image'));
    }
    const clipboardData = await getDataFromClipboard();
    if (isItemsAndPosterClipboardDataValid(clipboardData)) {
      await this.pasteBackgroundItemIfInClipboard(clipboardData, e, onSuccess);
      await this.pasteAudioItemIfInClipboard(clipboardData, e, onSuccess);
      await this.pasteInternalItemsAndPosterDataIfInClipboard(clipboardData, e, clipboardPasteInfo, onSuccess);
    } else {
      if (clipboardPasteInfo.isPasteFromRightClick) {
        const files = await this.getExternalCopiedimageFromRightClick();
        if (files === null) {
          return;
        }
        file = files.length > 0 ? files[0] : undefined;
      }
      if (file) {
        await this.pasteExternalImageItemIfInClipboard(file, onSuccess);
        return;
      } else if (await this.addedQRCodeItemFromClipboard()) {
        return;
      } else {
        const text = await getExternalTextItemDataFromClipboard(clipboardData as string);
        if (text) {
          this.pasteExternalTextItemIfInClipboard(text, e, onSuccess);
          return;
        }
      }
      getPosterUi().showMessageGrowl({
        text: window.i18next.t('pmwjs_pasted_content_not_supported'),
        icon: 'icon-poster-print',
        type: GROWL_TYPE.DANGER,
        interval: 2000,
        showCloseIcon: true,
      });
    }
  }
  public async getExternalCopiedimageFromRightClick(): Promise<File[] | null> {
    try {
      const items = await navigator.clipboard.read();
      const allTypePromises = items.flatMap((item) =>
        item.types.map(async (type) => {
          const blob = await item.getType(type);
          if (blob.type.startsWith('image/')) {
            const extension = blob.type.split('/')[1] || 'png';
            return new File([blob], `pasted.${extension}`, {type: blob.type});
          }
          return null;
        })
      );

      return (await Promise.all(allTypePromises)).filter(Boolean) as File[];
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        getPosterUi().showMessageGrowl({
          text: isDesktop
            ? window.i18next.t('pmwjs_clipboard_access_denied_with_shortcut', {shortcut: makeShortcut(['Ctrl', 'V'])})
            : window.i18next.t('pmwjs_clipboard_access_denied'),
          icon: 'icon-poster-print',
          type: GROWL_TYPE.DANGER,
          interval: 2000,
          showCloseIcon: true,
        });
      }
      return null;
    }
  }

  public async pasteExternalImageItemIfInClipboard(image: File | undefined, onSuccess?: () => void): Promise<void> {
    if (!image) {
      return;
    }
    const visibleBounds = !isEditorMobileVariant() ? getVisiblePageBounds(this.page) : null;
    await uploadFiles(
      {
        onFileUpload: (type: UserMediaType, data: UploadFileDataTypes): void => {
          const item = getSingleUploadedMediaItem(data);
          if (!window.posterEditor?.whiteboard?.getCurrentPage() || !item) {
            return;
          }
          if (isTempImageData(item)) {
            void getPosterUi().onAddItems([{...item, ...(visibleBounds && {top: visibleBounds.top, left: visibleBounds.left})}]);
          } else {
            if (!isUserLoggedIn()) {
              getPosterUi().updateUnlinkedMediaState([getMediaStorageForbackendData(item, type)]);
            }
            const itemData = userMediaVOToElementData(type, item);
            void getPosterUi().onAddItems([{...itemData, ...(visibleBounds && {top: visibleBounds.top, left: visibleBounds.left})}]);
          }
        },
        onUploadError: onPastedImageUploadError,
        onAfterUpload: onAfterPastedImageUpload,
      },
      [image],
      {
        shouldLoadDataUrlImage: true,
      }
    );
    if (onSuccess) {
      onSuccess();
    }
  }

  public async addedQRCodeItemFromClipboard(): Promise<boolean> {
    const clipboardItems = await navigator.clipboard.read();
    const item = clipboardItems.find((item) => item.types.includes(PMW_CUSTOM_CLIPBOARD_MIMETYPE));
    if (item?.types.includes(PMW_CUSTOM_CLIPBOARD_MIMETYPE)) {
      const blob = await item.getType(PMW_CUSTOM_CLIPBOARD_MIMETYPE);
      const qrCode = await blob.text();
      const visibleBounds = !isEditorMobileVariant() ? getVisiblePageBounds(this.page) : null;
      addQrCodeToPage(qrCode, visibleBounds?.top, visibleBounds?.left);
      return true;
    }
    return false;
  }

  public pasteExternalTextItemIfInClipboard(text: string, e?: Event | React.MouseEvent<HTMLElement>, onSuccess?: () => void): void {
    if (e) {
      e.preventDefault();
    }

    const script = getScriptForText(text);
    const visibleBounds = !isEditorMobileVariant() ? getVisiblePageBounds(this.page) : null;
    const textItemData = {
      type: ElementDataType.TEXT,
      text: text,
      ...(visibleBounds && {top: visibleBounds.top, left: visibleBounds.left}),
    } as ItemData;
    void this.page.items.addItems.addTextItem(textItemData, {
      textStyles: {
        script: script,
        fontFamily: script ? getRecommendedFont(script) : DEFAULT_TEXT_FONT_FAMILY,
      },
    });
    if (onSuccess) {
      onSuccess();
    }
  }

  public async pasteBackgroundItemIfInClipboard(data: unknown, e?: Event | React.MouseEvent<HTMLElement>, onSuccess?: () => void): Promise<void> {
    const backgroundData = getBackgroundItemDataFromClipboard(data);
    if (!backgroundData?.backgroundItemObject) {
      return;
    }

    if (!this.isPasteAllowed(backgroundData)) {
      getPosterUi().openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
      return;
    }

    if (e) {
      e.preventDefault();
    }

    try {
      const currentTime = this.page.clock.getCurrentTime();
      await this.page.items.pasteBackgroundAfterActive(backgroundData.backgroundItemObject, currentTime, {
        width: backgroundData.posterInfo.width,
        height: backgroundData.posterInfo.height,
      });
      if (onSuccess) {
        onSuccess();
      }
    } catch {
      getPosterUi().openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
    }
  }

  public async pasteAudioItemIfInClipboard(data: unknown, e?: Event | React.MouseEvent<HTMLElement>, onSuccess?: () => void): Promise<void> {
    const getAudioItemsDataIfExists = await getAudioItemDataFromClipboard(data);
    if (!getAudioItemsDataIfExists) {
      return;
    }

    if (!this.isPasteAllowed(getAudioItemsDataIfExists, true)) {
      getPosterUi().openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
    }

    if (e) {
      e.preventDefault();
    }

    try {
      if (getAudioItemsDataIfExists.audioItemObject) {
        await this.page.items.addItems.addAudioItemFromAudioItemObject(getAudioItemsDataIfExists.audioItemObject);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch {
      getPosterUi().openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
    }
  }

  public isPasteAllowed(graphicItemsAndPoster: PosterClipboardData, isForAudioItem = false): boolean {
    if (isForAudioItem) {
      return window.PMW.currentUserOnPageLoad?.isSuper || graphicItemsAndPoster.key === this.page.poster.copyKey || !getUserId() || !isUserDesigner();
    }

    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      graphicItemsAndPoster.key === this.page.poster.copyKey ||
      !graphicItemsAndPoster.posterInfo.idGalleryTemplate ||
      graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator === getUserId() ||
      !getUserId() ||
      !isUserDesigner()
    );
  }

  public isItemPasteAllowed(copyKey: string, idOriginalOwner?: number, templateCreator?: number): boolean {
    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      !getUserId() ||
      !isUserDesigner() ||
      copyKey === this.page.poster.copyKey ||
      idOriginalOwner === getUserId() ||
      templateCreator === getUserId() ||
      !(templateCreator || idOriginalOwner)
    );
  }

}

/**
 * eventCanvasOffset is already in absolute page coordinates, so the selection's anchor lands
 * exactly where the popup menu was opened. Returns null when the paste didn't come from a
 * right-click/long-press or has no usable canvas point (e.g. the timeline context menu), in
 * which case paste falls back to keyboard positioning.
 */
const getRightClickPastePoint = (clipboardPasteInfo: ClipboardPasteInfo, selection: PosterClipboardData['selection']): RightClickPastePoint | null => {
  if (!clipboardPasteInfo.isPasteFromRightClick) {
    return null;
  }
  const x = clipboardPasteInfo.eventCanvasOffset?.left;
  const y = clipboardPasteInfo.eventCanvasOffset?.top;
  const {left, top} = selection;
  if (x === undefined || y === undefined || typeof left !== 'number' || typeof top !== 'number') {
    return null;
  }
  return {x, y, selectionLeft: left, selectionTop: top};
};

const getBoundsOfItems = (graphicItems: ItemType[]): VisiblePageBounds => {
  const bounds: VisiblePageBounds = {left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity};
  for (const graphicItem of graphicItems) {
    const left = graphicItem.fabricObject.getX();
    const top = graphicItem.fabricObject.getY();
    bounds.left = Math.min(bounds.left, left);
    bounds.top = Math.min(bounds.top, top);
    bounds.right = Math.max(bounds.right, left + graphicItem.fabricObject.getScaledWidth());
    bounds.bottom = Math.max(bounds.bottom, top + graphicItem.fabricObject.getScaledHeight());
  }
  return bounds;
};

/**
 * An existing item counts as a copy of the pasted item when it has the same type and content
 * and sits at the paste target position (once the paste displacement being probed is applied).
 */
const isCopyOfItemAtPosition = (existingItem: ItemType, pastedItem: ItemType, displacement: number): boolean => {
  if (existingItem.gitype !== pastedItem.gitype) {
    return false;
  }
  const isAtTargetPosition =
    Math.abs(existingItem.getX() - (pastedItem.fabricObject.getX() + displacement)) < PASTE_POSITION_MATCH_TOLERANCE &&
    Math.abs(existingItem.getY() - (pastedItem.fabricObject.getY() + displacement)) < PASTE_POSITION_MATCH_TOLERANCE;
  return isAtTargetPosition && haveItemsSameContent(existingItem, pastedItem);
};

/**
 * Text items match on their text plus a loose relative size check — their rendered box drifts
 * a few pixels with fonts/wrapping, but a noticeably rescaled same-text item is a different
 * design element. Other item types match on their scaled size.
 */
const haveItemsSameContent = (existingItem: ItemType, pastedItem: ItemType): boolean => {
  if (existingItem.isText() && pastedItem.isText()) {
    return existingItem.text === pastedItem.text && areItemSizesSimilar(existingItem, pastedItem);
  }
  return (
    Math.abs(existingItem.fabricObject.getScaledWidth() - pastedItem.fabricObject.getScaledWidth()) < PASTE_POSITION_MATCH_TOLERANCE &&
    Math.abs(existingItem.fabricObject.getScaledHeight() - pastedItem.fabricObject.getScaledHeight()) < PASTE_POSITION_MATCH_TOLERANCE
  );
};

const areItemSizesSimilar = (existingItem: ItemType, pastedItem: ItemType): boolean => {
  const existingWidth = existingItem.fabricObject.getScaledWidth();
  const pastedWidth = pastedItem.fabricObject.getScaledWidth();
  const existingHeight = existingItem.fabricObject.getScaledHeight();
  const pastedHeight = pastedItem.fabricObject.getScaledHeight();
  return (
    Math.abs(existingWidth - pastedWidth) / Math.max(existingWidth, pastedWidth) <= COPY_MAX_RELATIVE_SIZE_DIFFERENCE &&
    Math.abs(existingHeight - pastedHeight) / Math.max(existingHeight, pastedHeight) <= COPY_MAX_RELATIVE_SIZE_DIFFERENCE
  );
};

const getDataFromClipboard = async (): Promise<unknown> => {
  let clipText: string;
  let itemsAndPoster;
  try {
    clipText = await readFromClipboard();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'NotAllowedError') {
      getPosterUi().showMessageGrowl({
        text: isDesktop
          ? window.i18next.t('pmwjs_clipboard_access_denied_with_shortcut', {shortcut: makeShortcut(['Ctrl', 'V'])})
          : window.i18next.t('pmwjs_clipboard_access_denied'),
        icon: 'icon-poster-print',
        type: GROWL_TYPE.DANGER,
        interval: 2000,
        showCloseIcon: true,
      });
    }
    return undefined;
  }

  try {
    itemsAndPoster = JSON.parse(clipText) as unknown;
  } catch (err) {
    return clipText;
  }

  return itemsAndPoster;
};

const onAfterPastedImageUpload = (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse): void => {
  if (!isUserLoggedIn() && !isArray(data)) {
    const mediaItem = getMediaStorageForbackendData(data, type);
    getPosterUi().updateUnlinkedMediaState([mediaItem]);
  }
};

const onPastedImageUploadError = (): void => {
  getPosterUi().openErrorModal.bind({message: window.i18next.t('pmwjs_cannot_upload_error_msg')});
};

const getExternalTextDataFromClipboard = async (clipText: string): Promise<string | undefined> => {
  try {
    if (isJson(clipText)) {
      return undefined;
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'NotAllowedError') {
      getPosterUi().showMessageGrowl({
        text: isDesktop
          ? window.i18next.t('pmwjs_clipboard_access_denied_with_shortcut', {shortcut: makeShortcut(['Ctrl', 'V'])})
          : window.i18next.t('pmwjs_clipboard_access_denied'),
        icon: 'icon-poster-print',
        type: GROWL_TYPE.DANGER,
        interval: 2000,
        showCloseIcon: true,
      });
      return undefined;
    }
  }

  return clipText;
};

const getItemsAndPosterDataFromClipboard = async (data: unknown): Promise<PosterClipboardData | undefined> => {
  const validateditemsAndPoster = getValidatedItemsAndPosterClipboardData(data);
  if (validateditemsAndPoster) {
    return validateditemsAndPoster;
  }

  return undefined;
};

const getAudioItemDataFromClipboard = async (data: unknown): Promise<PosterClipboardData | undefined> => {
  const validateAudioItemData = getItemsAndPosterClipboardDataForAudio(data);
  if (validateAudioItemData) {
    return validateAudioItemData;
  }

  return undefined;
};

const getExternalTextItemDataFromClipboard = async (data: string): Promise<string | undefined> => {
  const text = await getExternalTextDataFromClipboard(data);

  if (!text || (text && !text.trim())) {
    return undefined;
  }

  return text.trim();
};

export const getItemsAndPosterClipboardDataForAudio = (data: unknown): PosterClipboardData | undefined => {
  if (isObject(data) && 'key' in data && 'version' in data && data.version !== PASTE_ITEMS_VERSION && 'audioItemObject' in data) {
    getPosterUi().showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_audio_clip_pasting_error'),
    });
    return undefined;
  }

  if (isObject(data) && 'key' in data && 'audioItemObject' in data && 'posterInfo' in data) {
    return data as PosterClipboardData;
  }
  return undefined;
};

const getBackgroundItemDataFromClipboard = (data: unknown): PosterClipboardData | undefined => {
  if (isObject(data) && 'key' in data && 'backgroundItemObject' in data && 'posterInfo' in data) {
    return data as PosterClipboardData;
  }
  return undefined;
};

export const getValidatedItemsAndPosterClipboardData = (data: unknown): PosterClipboardData | undefined => {
  if (isObject(data) && 'key' in data && 'version' in data && data.version !== PASTE_ITEMS_VERSION && 'graphicItemObjects' in data) {
    getPosterUi().showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_items_pasting_error'),
    });
    return undefined;
  }

  if (isObject(data) && 'key' in data && 'graphicItemObjects' in data && 'posterInfo' in data && 'selection' in data) {
    return data as PosterClipboardData;
  }
  return undefined;
};

export const isItemsAndPosterClipboardDataValid = (data: unknown): boolean => {
  if (!isObject(data) || !('key' in data)) {
    return false;
  }

  if ('version' in data && data.version !== PASTE_ITEMS_VERSION) {
    if ('audioItemObject' in data || 'graphicItemObjects' in data) {
      getPosterUi().showMessageGrowl({
        type: GROWL_TYPE.DANGER,
        text: window.i18next.t('audioItemObject' in data ? 'pmwjs_audio_clip_pasting_error' : 'pmwjs_items_pasting_error'),
      });
    }
    return false;
  }

  if ('posterInfo' in data) {
    if ('backgroundItemObject' in data) {
      return true;
    }
    if ('audioItemObject' in data) {
      return true;
    }
    if ('graphicItemObjects' in data && 'selection' in data) {
      return true;
    }
  }

  return false;
};

// export const isAudioInClipboardData = (data: unknown): boolean => {
//   if (!isObject(data) || !('key' in data)) {
//     return false;
//   }
//   return 'audioItemObject' in data;
// };
